import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "methodRadio",
    "giverRow",
    "checkbox",
    "warning",
    "warningTitle",
    "warningText",
    "costRow",
    "freeRow",
    "totalCost",
    "count",
    "mailOption",
    "emailOption",
    "downloadOption",
    "submitButton",
    "submitHint"
  ]

  static values = {
    pricePerCard: Number
  }

  connect() {
    this.updateMethod()
  }

  updateMethod() {
    const method = this.getSelectedMethod()
    this.updatePricingDisplay(method)
    this.updateWarnings(method)
    this.updateSubmitButton(method)
  }

  updateSelection() {
    // Called when checkboxes change - update warnings
    const method = this.getSelectedMethod()
    this.updateWarnings(method)
    this.updatePricingDisplay(method)
  }

  getSelectedMethod() {
    const selected = this.methodRadioTargets.find(radio => radio.checked)
    return selected ? selected.value : "mail"
  }

  updatePricingDisplay(method) {
    if (method === "mail") {
      this.costRowTarget.classList.remove("hidden")
      this.freeRowTarget.classList.add("hidden")

      // Update total based on eligible recipients
      const eligibleCount = this.getEligibleCount(method)
      const total = (eligibleCount * this.pricePerCardValue).toFixed(2)
      this.totalCostTarget.innerHTML = `$${total}`
      this.countTarget.textContent = eligibleCount
    } else {
      this.costRowTarget.classList.add("hidden")
      this.freeRowTarget.classList.remove("hidden")

      // Update count for free methods
      const eligibleCount = this.getEligibleCount(method)
      this.countTarget.textContent = eligibleCount
    }
  }

  getEligibleCount(method) {
    let count = 0
    this.checkboxTargets.forEach(checkbox => {
      if (!checkbox.checked) return

      const row = checkbox.closest("[data-delivery-method-target='giverRow']")
      if (!row) return

      const hasAddress = row.dataset.hasAddress === "true"
      const hasEmail = row.dataset.hasEmail === "true"

      if (method === "mail" && hasAddress) count++
      else if (method === "email" && hasEmail) count++
      else if (method === "download") count++
    })
    return count
  }

  getSelectedRecipients() {
    const selected = []
    this.checkboxTargets.forEach(checkbox => {
      if (!checkbox.checked) return

      const row = checkbox.closest("[data-delivery-method-target='giverRow']")
      if (!row) return

      selected.push({
        key: row.dataset.giverKey,
        hasAddress: row.dataset.hasAddress === "true",
        hasEmail: row.dataset.hasEmail === "true"
      })
    })
    return selected
  }

  updateWarnings(method) {
    const selected = this.getSelectedRecipients()
    if (selected.length === 0) {
      this.warningTarget.classList.add("hidden")
      return
    }

    let missingCount = 0
    let missingType = ""

    if (method === "mail") {
      missingCount = selected.filter(r => !r.hasAddress).length
      missingType = "mailing addresses"
    } else if (method === "email") {
      missingCount = selected.filter(r => !r.hasEmail).length
      missingType = "email addresses"
    }
    // Download has no requirements

    if (missingCount > 0) {
      this.warningTarget.classList.remove("hidden")
      this.warningTarget.setAttribute("role", "alert")
      this.warningTarget.setAttribute("aria-live", "polite")
      this.warningTitleTarget.textContent = `${missingCount} recipient${missingCount === 1 ? '' : 's'} will be skipped`
      this.warningTextTarget.textContent = `${missingCount === 1 ? 'This recipient doesn\'t have' : 'These recipients don\'t have'} ${missingType}. Add ${missingType} or they'll be excluded.`
    } else {
      this.warningTarget.classList.add("hidden")
      this.warningTarget.removeAttribute("role")
      this.warningTarget.removeAttribute("aria-live")
    }
  }

  updateSubmitButton(method) {
    const eligible = this.getEligibleCount(method)

    if (eligible === 0) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
    } else {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
    }

    // Update hint text based on method
    if (method === "mail") {
      this.submitHintTarget.textContent = "You'll be able to edit messages before sending"
    } else if (method === "email") {
      this.submitHintTarget.textContent = "Cards will be sent as beautiful email thank yous"
    } else {
      this.submitHintTarget.textContent = "You'll get PDFs to print at home"
    }
  }
}
